import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useTrip, useCreateShareableLink, TripData, HotelPricing } from "@/hooks/useTrips";
import { useProfile } from "@/hooks/useProfile";
import { ViewMode } from "@/components/itinerary/ItineraryPreview";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { CoverPhotoBackground } from "@/components/itinerary/CoverPhotoBackground";
import { extractCoverPhoto, getCoverPhotoUrl } from "@/hooks/useUnsplash";
import { supabase } from "@/integrations/supabase/client";
import { FlightCard } from "@/components/trips/items/FlightCard";
import { HotelCard } from "@/components/trips/items/HotelCard";
import { CarCard } from "@/components/trips/items/CarCard";
import { ActivityCard } from "@/components/trips/items/ActivityCard";
import { CruiseCard } from "@/components/trips/items/CruiseCard";
import {
  ArrowLeft,
  Download,
  Mail,
  Link as LinkIcon,
  Copy,
  Check,
  Loader2,
  Share2,
  Calendar,
  Layers,
  Plane,
  LogOut,
  MapPin
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { safeFormat } from "@/lib/formatDateTime";
import { FlightSegment, HotelReservation, CarReservation, Activity, CruiseReservation } from "@/hooks/useTrips";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getCompanyLogoUrl } from "@/lib/companyLogos";

// Simplified checkout card for the by-date view
function HotelCheckoutCard({ hotel }: { hotel: HotelReservation }) {
  const displayName = hotel.hotel_name || hotel.address || hotel.city || "Hotel";
  const logoUrl = getCompanyLogoUrl(hotel.hotel_name, 64);

  const formatTime = (timeStr: string | undefined) => {
    if (!timeStr) return "Before 11:00 AM";
    // Handle ISO format
    const isoTimeMatch = timeStr.match(/T(\d{2}:\d{2})/);
    if (isoTimeMatch) {
      const [hours, minutes] = isoTimeMatch[1].split(':').map(Number);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
    }
    return timeStr;
  };

  return (
    <Card className="border-dashed border-orange-300 bg-orange-50/50">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <div className="w-10 h-10 rounded bg-white border border-orange-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              <img
                src={logoUrl}
                alt={displayName}
                className="w-6 h-6 object-contain mix-blend-multiply"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    parent.innerHTML = `<span class="text-xs font-bold text-orange-600">${displayName.substring(0, 2).toUpperCase()}</span>`;
                  }
                }}
              />
            </div>
          ) : (
            <div className="w-10 h-10 rounded bg-white border border-orange-200 flex items-center justify-center flex-shrink-0">
              <LogOut className="w-5 h-5 text-orange-500" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 text-xs">
                Checkout
              </Badge>
              <span className="font-medium truncate">{displayName}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
              <span>{formatTime(hotel.check_out_time)}</span>
              {hotel.address && (
                <span className="flex items-center gap-1 truncate">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{hotel.city || hotel.address}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Compact date label for by-type view
function DateLabel({ date, label }: { date: string | undefined; label?: string }) {
  if (!date) return null;
  const formattedDate = safeFormat(date, "EEE, MMM d");
  if (formattedDate === "—") return null;
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
      <Calendar className="w-3 h-3" />
      <span>{label ? `${label}: ` : ''}{formattedDate}</span>
    </div>
  );
}

// Extract date portion from an ISO datetime string (e.g., "2024-04-24T10:00:00+08:00" -> "2024-04-24")
// This extracts the LOCAL date as stored in the string, without timezone conversion
function extractDateFromISO(isoString: string): string | null {
  const match = isoString.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

// Get the local date for an item (for grouping by date)
// Priority: explicit date field > date portion of ISO time string
function getLocalDate(explicitDate?: string, timeField?: string): string {
  // Use explicit date if provided
  if (explicitDate && /^\d{4}-\d{2}-\d{2}/.test(explicitDate)) {
    return explicitDate.slice(0, 10);
  }
  // Extract date from ISO time string if it contains one
  if (timeField && timeField.includes('T')) {
    const extracted = extractDateFromISO(timeField);
    if (extracted) return extracted;
  }
  return '';
}

// Helper to parse datetime for sorting (using local date + time)
function parseDateTimeForSorting(date?: string, time?: string): Date {
  const localDate = getLocalDate(date, time);
  if (!localDate) return new Date(0);

  try {
    // Extract just the time portion (HH:MM) without timezone conversion
    let timeStr = '00:00';
    if (time) {
      // Handle ISO format: "2024-04-24T10:30:00+08:00"
      const isoTimeMatch = time.match(/T(\d{2}:\d{2})/);
      if (isoTimeMatch) {
        timeStr = isoTimeMatch[1];
      } else {
        // Handle simple time format: "10:30" or "10:30:00"
        const simpleTimeMatch = time.match(/^(\d{1,2}:\d{2})/);
        if (simpleTimeMatch) {
          timeStr = simpleTimeMatch[1].padStart(5, '0');
        }
      }
    }

    // Create a date without timezone conversion by parsing as local
    const [year, month, day] = localDate.split('-').map(Number);
    const [hours, minutes] = timeStr.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  } catch {
    return new Date(0);
  }
}

// Timeline item type for by-date view
type TimelineItem = {
  id: string;
  type: 'flight' | 'hotel-checkin' | 'hotel-checkout' | 'car-pickup' | 'car-dropoff' | 'cruise' | 'activity';
  date: string;
  sortTime: Date;
  data: FlightSegment | HotelReservation | CarReservation | Activity | CruiseReservation;
  pricing?: unknown;
};

// PreviewByDate component for chronological view
function PreviewByDate({ tripData }: { tripData: TripData | null }) {
  if (!tripData) return null;

  const items: TimelineItem[] = [];

  // Add flights - use local date from departure location
  tripData.flights?.forEach((flight, index) => {
    const localDate = getLocalDate(flight.departure_date, flight.departure_time);
    items.push({
      id: `flight-${index}`,
      type: 'flight',
      date: localDate,
      sortTime: parseDateTimeForSorting(flight.departure_date, flight.departure_time),
      data: flight,
    });
  });

  // Add hotels (check-in and check-out as separate events)
  tripData.hotels?.forEach((hotel, index) => {
    const checkInDate = getLocalDate(hotel.check_in_date, hotel.check_in_time);
    const checkOutDate = getLocalDate(hotel.check_out_date, hotel.check_out_time);
    if (checkInDate) {
      items.push({
        id: `hotel-in-${index}`,
        type: 'hotel-checkin',
        date: checkInDate,
        sortTime: parseDateTimeForSorting(hotel.check_in_date, hotel.check_in_time),
        data: hotel,
        pricing: tripData.hotel_pricings?.find(p => p.hotel_name === hotel.hotel_name),
      });
    }
    if (checkOutDate) {
      items.push({
        id: `hotel-out-${index}`,
        type: 'hotel-checkout',
        date: checkOutDate,
        sortTime: parseDateTimeForSorting(hotel.check_out_date, hotel.check_out_time),
        data: hotel,
      });
    }
  });

  // Add cars
  tripData.cars?.forEach((car, index) => {
    const pickupDate = getLocalDate(car.pickup_date, car.pickup_time);
    const dropoffDate = getLocalDate(car.dropoff_date, car.dropoff_time);
    if (pickupDate) {
      items.push({
        id: `car-pickup-${index}`,
        type: 'car-pickup',
        date: pickupDate,
        sortTime: parseDateTimeForSorting(car.pickup_date, car.pickup_time),
        data: car,
      });
    }
    if (dropoffDate) {
      items.push({
        id: `car-dropoff-${index}`,
        type: 'car-dropoff',
        date: dropoffDate,
        sortTime: parseDateTimeForSorting(car.dropoff_date, car.dropoff_time),
        data: car,
      });
    }
  });

  // Add activities
  tripData.activities?.forEach((activity, index) => {
    const activityDate = getLocalDate(activity.date, activity.time);
    if (activityDate) {
      items.push({
        id: `activity-${index}`,
        type: 'activity',
        date: activityDate,
        sortTime: parseDateTimeForSorting(activity.date, activity.time),
        data: activity,
      });
    }
  });

  // Add cruises
  tripData.cruises?.forEach((cruise, index) => {
    const cruiseDate = getLocalDate(cruise.departure_date, cruise.departure_time);
    if (cruiseDate) {
      items.push({
        id: `cruise-${index}`,
        type: 'cruise',
        date: cruiseDate,
        sortTime: parseDateTimeForSorting(cruise.departure_date, cruise.departure_time),
        data: cruise,
      });
    }
  });

  // Sort by date and time
  items.sort((a, b) => a.sortTime.getTime() - b.sortTime.getTime());

  // Group by date
  const itemsByDate = items.reduce((acc, item) => {
    if (!acc[item.date]) {
      acc[item.date] = [];
    }
    acc[item.date].push(item);
    return acc;
  }, {} as Record<string, TimelineItem[]>);

  const sortedDates = Object.keys(itemsByDate).sort();

  return (
    <div className="space-y-8">
      {sortedDates.map((date) => (
        <section key={date} className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="gradient-accent text-white px-4 py-2 rounded-lg">
              <div className="text-sm">
                {safeFormat(date, "EEE")}
              </div>
              <div className="text-lg font-semibold">
                {safeFormat(date, "MMM d")}
              </div>
            </div>
            <h2 className="text-xl font-semibold">
              {safeFormat(date, "MMMM d, yyyy")}
            </h2>
          </div>
          <div className="space-y-4">
            {itemsByDate[date].map((item) => {
              if (item.type === 'flight') {
                return (
                  <FlightCard
                    key={item.id}
                    flight={item.data as FlightSegment}
                    allFlights={tripData.flights || []}
                    readOnly
                    tripPassengerName={tripData.passenger_name}
                  />
                );
              }
              if (item.type === 'hotel-checkin') {
                return (
                  <HotelCard
                    key={item.id}
                    hotel={item.data as HotelReservation}
                    pricing={item.pricing as HotelPricing | undefined}
                    readOnly
                  />
                );
              }
              if (item.type === 'hotel-checkout') {
                return (
                  <HotelCheckoutCard
                    key={item.id}
                    hotel={item.data as HotelReservation}
                  />
                );
              }
              if (item.type === 'car-pickup' || item.type === 'car-dropoff') {
                return <CarCard key={item.id} car={item.data as CarReservation} readOnly />;
              }
              if (item.type === 'activity') {
                return <ActivityCard key={item.id} activity={item.data as Activity} readOnly />;
              }
              if (item.type === 'cruise') {
                return <CruiseCard key={item.id} cruise={item.data as CruiseReservation} readOnly />;
              }
              return null;
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export default function Preview() {
  const { tripId } = useParams<{ tripId: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { data: trip, isLoading: tripLoading } = useTrip(tripId);
  const { data: profile } = useProfile();
  const createShareableLink = useCreateShareableLink();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // PDF state
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>("by-type");

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  const handleDownload = async () => {
    if (!trip) return;

    setGeneratingPdf(true);
    try {
      const tripData = trip.trip_data as TripData | null;
      const startDate = trip.start_date ? safeFormat(trip.start_date, "MMM d, yyyy") : undefined;
      const endDate = trip.end_date ? safeFormat(trip.end_date, "MMM d, yyyy") : undefined;

      // Get cover photo URL for PDF (using pdf size preset)
      const coverPhoto = extractCoverPhoto(trip);
      const coverPhotoUrl = coverPhoto ? getCoverPhotoUrl(coverPhoto.url, 'pdf') : null;

      const { data, error } = await supabase.functions.invoke("generate-itinerary-pdf", {
        body: {
          tripName: trip.display_name,
          destination: trip.destination,
          startDate,
          endDate,
          tripData: tripData || {},
          coverPhotoUrl,
          coverPhotoPhotographer: coverPhoto?.photographerName,
          coverPhotoPhotographerUsername: coverPhoto?.photographerUsername,
          viewMode,
        },
      });

      if (error) throw error;

      // Download the PDF
      const link = document.createElement("a");
      link.href = data.pdf;
      link.download = `${trip.display_name.replace(/[^a-z0-9]/gi, "_")}_itinerary.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("PDF downloaded successfully");
    } catch (error: unknown) {
      console.error("Error generating PDF:", error);
      // Try to get detailed error from FunctionsHttpError
      if (error && typeof error === 'object' && 'context' in error) {
        const ctx = (error as { context?: { json?: () => Promise<unknown> } }).context;
        if (ctx?.json) {
          try {
            const errorBody = await ctx.json();
            console.error("PDF error details:", errorBody);
          } catch { /* ignore */ }
        }
      }
      toast.error("Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSendEmail = async () => {
    if (!trip || !recipientEmail) return;

    if (!recipientEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSendingEmail(true);
    try {
      const tripData = trip.trip_data as TripData | null;
      const startDate = trip.start_date ? safeFormat(trip.start_date, "MMM d, yyyy") : undefined;
      const endDate = trip.end_date ? safeFormat(trip.end_date, "MMM d, yyyy") : undefined;

      const { error } = await supabase.functions.invoke("send-itinerary-email", {
        body: {
          tripId: trip.id,
          recipientEmail,
          tripName: trip.display_name,
          destination: trip.destination,
          startDate,
          endDate,
          tripData: tripData || {},
        },
      });

      if (error) throw error;

      toast.success(`Itinerary sent to ${recipientEmail}`);
      setEmailDialogOpen(false);
      setRecipientEmail("");
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email. Make sure the email domain is verified in Resend.");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCreateLink = async () => {
    if (!tripId) return;

    try {
      const result = await createShareableLink.mutateAsync({ tripId });
      const url = `${window.location.origin}/share/${result.share_token}`;
      setShareUrl(url);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;

    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied to clipboard");

    setTimeout(() => setCopied(false), 2000);
  };

  // Calculate trip stats
  const tripData = trip?.trip_data as TripData | null;
  const flightCount = tripData?.flights?.length || 0;
  const hotelCount = tripData?.hotels?.length || 0;
  const activityCount = tripData?.activities?.length || 0;
  const carCount = tripData?.cars?.length || 0;

  // Calculate total cost (simplified)
  const calculateTotalCost = () => {
    let total = 0;
    tripData?.flights?.forEach(f => {
      if (f.price) total += f.price;
    });
    tripData?.hotels?.forEach(h => {
      // Find matching pricing
      const pricing = tripData?.hotel_pricings?.find(p => p.hotel_name === h.hotel_name);
      if (pricing?.retail_price) {
        const nights = h.check_in_date && h.check_out_date
          ? Math.ceil((new Date(h.check_out_date).getTime() - new Date(h.check_in_date).getTime()) / (1000 * 60 * 60 * 24))
          : 1;
        total += pricing.retail_price * nights;
      }
    });
    tripData?.cars?.forEach(c => {
      if (c.total_cost) total += c.total_cost;
    });
    tripData?.activities?.forEach(a => {
      if (a.cost) total += a.cost;
    });
    return total;
  };

  const totalCost = calculateTotalCost();

  if (authLoading || tripLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-display text-2xl font-bold text-foreground">Trip Not Found</h1>
            <p className="mt-2 text-muted-foreground">This trip doesn't exist or you don't have access to it.</p>
            <Link to="/trips" className="mt-4 inline-block">
              <Button variant="accent">Back to Trips</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" id="preview-content">
      {/* Sticky Header */}
      <div className="bg-card border-b border-border sticky top-0 z-10 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center justify-between sm:justify-start gap-4 w-full sm:w-auto">
            <Link to={`/trips/${tripId}`}>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2 font-display text-xl font-bold text-primary">
              <Plane className="h-6 w-6 text-accent" />
              <span>TripDoc</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            {/* View Toggle - Pill Style */}
            <div className="flex bg-muted rounded-lg p-1 w-full sm:w-auto">
              <button
                onClick={() => setViewMode('by-date')}
                className={`flex-1 sm:flex-none px-3 py-1.5 rounded flex items-center justify-center gap-2 text-xs sm:text-sm transition-colors ${
                  viewMode === 'by-date' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Calendar className="w-4 h-4" />
                By Date
              </button>
              <button
                onClick={() => setViewMode('by-type')}
                className={`flex-1 sm:flex-none px-3 py-1.5 rounded flex items-center justify-center gap-2 text-xs sm:text-sm transition-colors ${
                  viewMode === 'by-type' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Layers className="w-4 h-4" />
                By Type
              </button>
            </div>

            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" onClick={handleCreateLink} className="sm:w-auto">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Share Itinerary</DialogTitle>
                  <DialogDescription>
                    Anyone with this link can view the itinerary
                  </DialogDescription>
                </DialogHeader>

                {shareUrl ? (
                  <div className="flex gap-2">
                    <Input value={shareUrl} readOnly className="flex-1" />
                    <Button variant="outline" size="icon" onClick={handleCopyLink}>
                      {copied ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent" />
                  </div>
                )}
              </DialogContent>
            </Dialog>

            <Button onClick={handleDownload} disabled={generatingPdf} className="sm:w-auto">
              {generatingPdf ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {generatingPdf ? "Generating..." : "Download PDF"}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <ErrorBoundary>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero Image */}
        {extractCoverPhoto(trip) && (
          <div className="mb-8 -mx-6 md:mx-0 md:rounded-lg overflow-hidden">
            <img
              src={getCoverPhotoUrl(extractCoverPhoto(trip)!.url, 'preview')}
              alt={trip.display_name}
              className="w-full h-64 object-cover"
            />
          </div>
        )}

        {/* Trip Header */}
        <div className="mb-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
            <div className="flex items-center gap-2 font-display text-lg font-bold text-primary">
              <Plane className="h-5 w-5 text-accent" />
              <span>TripDoc</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {trip.start_date && safeFormat(trip.start_date, "MMMM d, yyyy")}
            </div>
          </div>

          <h1 className="font-display text-3xl font-bold mb-2">{trip.display_name}</h1>
          {trip.destination && (
            <p className="text-lg text-muted-foreground mb-4">{trip.destination}</p>
          )}

          {trip.start_date && trip.end_date && (
            <p className="text-muted-foreground">
              {safeFormat(trip.start_date, "MMMM d, yyyy")} - {safeFormat(trip.end_date, "MMMM d, yyyy")}
            </p>
          )}
        </div>

        {/* Trip Summary */}
        <div className="bg-muted/50 rounded-lg p-6 mb-8">
          <h2 className="font-display text-xl font-semibold mb-4">Trip Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Flights</div>
              <div className="text-2xl font-semibold">{flightCount}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Hotels</div>
              <div className="text-2xl font-semibold">{hotelCount}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Activities</div>
              <div className="text-2xl font-semibold">{activityCount}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Total Cost</div>
              <div className="text-2xl font-semibold">${totalCost.toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* Itinerary Content - View by Type */}
        {viewMode === 'by-type' && (
          <>
            {/* Flights Section */}
            {tripData?.flights && tripData.flights.length > 0 && (
              <section className="mb-8">
                <h2 className="font-display text-xl font-semibold mb-4">Flights</h2>
                <div className="space-y-4">
                  {[...tripData.flights]
                    .sort((a, b) => parseDateTimeForSorting(a.departure_date, a.departure_time).getTime() - parseDateTimeForSorting(b.departure_date, b.departure_time).getTime())
                    .map((flight, index) => (
                    <div key={index}>
                      <DateLabel date={flight.departure_date} />
                      <FlightCard
                        flight={flight}
                        allFlights={tripData.flights || []}
                        readOnly
                        tripPassengerName={tripData.passenger_name}
                      />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Hotels Section */}
            {tripData?.hotels && tripData.hotels.length > 0 && (
              <section className="mb-8">
                <h2 className="font-display text-xl font-semibold mb-4">Accommodations</h2>
                <div className="space-y-4">
                  {[...tripData.hotels]
                    .sort((a, b) => parseDateTimeForSorting(a.check_in_date, a.check_in_time).getTime() - parseDateTimeForSorting(b.check_in_date, b.check_in_time).getTime())
                    .map((hotel, index) => {
                    const pricing = tripData?.hotel_pricings?.find(
                      (p) => p.hotel_name === hotel.hotel_name && p.check_in_date === hotel.check_in_date
                    );
                    return (
                      <div key={index}>
                        <DateLabel date={hotel.check_in_date} label="Check-in" />
                        <HotelCard hotel={hotel} pricing={pricing} readOnly />
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Car Rentals Section */}
            {tripData?.cars && tripData.cars.length > 0 && (
              <section className="mb-8">
                <h2 className="font-display text-xl font-semibold mb-4">Car Rentals</h2>
                <div className="space-y-4">
                  {[...tripData.cars]
                    .sort((a, b) => parseDateTimeForSorting(a.pickup_date, a.pickup_time).getTime() - parseDateTimeForSorting(b.pickup_date, b.pickup_time).getTime())
                    .map((car, index) => (
                    <div key={index}>
                      <DateLabel date={car.pickup_date} label="Pickup" />
                      <CarCard car={car} readOnly />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Activities Section */}
            {tripData?.activities && tripData.activities.length > 0 && (
              <section className="mb-8">
                <h2 className="font-display text-xl font-semibold mb-4">Activities & Experiences</h2>
                <div className="space-y-4">
                  {[...tripData.activities]
                    .sort((a, b) => parseDateTimeForSorting(a.date, a.time).getTime() - parseDateTimeForSorting(b.date, b.time).getTime())
                    .map((activity, index) => (
                    <div key={index}>
                      <DateLabel date={activity.date} />
                      <ActivityCard activity={activity} readOnly />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Cruises Section */}
            {tripData?.cruises && tripData.cruises.length > 0 && (
              <section className="mb-8">
                <h2 className="font-display text-xl font-semibold mb-4">Cruises</h2>
                <div className="space-y-4">
                  {[...tripData.cruises]
                    .sort((a, b) => parseDateTimeForSorting(a.departure_date, undefined).getTime() - parseDateTimeForSorting(b.departure_date, undefined).getTime())
                    .map((cruise, index) => (
                    <div key={index}>
                      <DateLabel date={cruise.departure_date} />
                      <CruiseCard cruise={cruise} readOnly />
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* Itinerary Content - View by Date */}
        {viewMode === 'by-date' && (
          <ErrorBoundary>
            <PreviewByDate tripData={tripData} />
          </ErrorBoundary>
        )}

        {/* Footer */}
        <div className="border-t border-border pt-8 mt-12 text-center text-sm text-muted-foreground">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Plane className="h-4 w-4 text-accent" />
            <span className="font-semibold text-foreground">TripDoc</span>
          </div>
          <p>Professional Travel Itineraries</p>
          <p className="mt-1">Generated on {safeFormat(new Date().toISOString(), "MMMM d, yyyy")}</p>
        </div>
      </div>
      </ErrorBoundary>
    </div>
  );
}
